<template>
    <WidgetSection
        ref="WidgetSection"
        :loaded="loaded"
        :loading="loading"
        :defaultToggle="defaultToggle"
        @loading-start="handleLoadingStart"
        loadingText="components.prospect_sessions.loading_text"
        :paywall="paywallActive"
        :unavailable="unavailable"
        :cssClass="`content-type-39 ${cssClassWidgetSection}`"
    >
        <template v-slot:section-title>
            <div class="title">
                {{ $t('components.prospect_sessions.name')}}
            </div>
        </template>

        <template v-slot:section-content>
            <div class="row row-content">
                <div class="sessions" v-scroll-shadow="{ directions: ['vertical']}">
                    <div class="title-sessions">
                        <div class="sessions-count">{{ $t('components.prospect_sessions.header.sessions', { count: sessions.length }) }}</div>
                        <div class="first-session" v-if="sessions.length > 1">{{ $t('components.prospect_sessions.header.first_session') }}: {{ epochToDisplayDate(sessions[sessions.length - 1].session_first_visit, true) }}</div>
                    </div>
                    <div class="list-actions" v-if="sessions.length > 1">
                        <button 
                            class="btn-link" 
                            @click="expandAll"
                            v-if="!isAllExpanded">
                            {{ $t('common.expand_all') }}
                        </button>
                        <button
                            class="btn-link"
                            @click="collapseAll"
                            v-if="!isAllCollapsed">
                            {{ $t('common.collapse_all') }}
                        </button>
                    </div>

                    <div 
                        class="session" 
                        :class="[expandedSessions.has(session.session) ? 'expanded' : 'collapsed']"
                        v-for="(session, index) in sessions" 
                        :key="session.session"
                    >

                        <div class="session-date" @click="toggleSession(session.session)">
                            <div class="date">{{ timestampToDisplayDate(session.session_first_visit, false ) }}</div>
                            <div class="time">{{ timestampToDisplayDate(session.session_first_visit, true, false, true) }}</div>
                        </div>

                        <div class="session-content">
                            <div class="session-title" @click="toggleSession(session.session)">
                                <div class="title-top">
                                    <div class="session-id" :class="[expandedSessions.has(session.session) ? 'expanded' : 'collapsed']">{{ $t('components.prospect_sessions.header.session_number', { count: sessions.length - index }) }}</div>
                                    <div class="btn-toggle">
                                        <div class="btn-toggle-icon" :class="expandedSessions.has(session.session) ? 'icon-close-dark' : 'icon-plus-dark'"></div>
                                    </div>
                                </div>
                                
                                <div class="title-bottom">
                                    <div class="p session-stats" :class="[expandedSessions.has(session.session) ? 'expanded' : 'collapsed']" v-if="session.pageviews.length">
                                        <span v-if="!expandedSessions.has(session.session)">
                                            {{ !!session.medium && $te('components.prospect_sessions.session.types.' + session.medium.toLowerCase()) ? $t('components.prospect_sessions.session.types.' + session.medium.toLowerCase()) : $t('components.prospect_sessions.session.types.direct')  }} - 
                                        </span>
                                        <span v-if="!expandedSessions.has(session.session) && session.term">"{{session.term}}" - </span>
                                        <span>
                                            {{ $t(('components.prospect_sessions.session.' + (session.pageviews.length > 1 ? 'pageviews' : 'pageview')), { count: session.pageviews.length }) }}
                                        </span>
                                        <span v-if="session.session_duration > 20">
                                            - {{ $t('components.prospect_sessions.session.duration', { duration: reformatDuration(session.session_duration) }) }}
                                        </span>
                                    </div>

                                    <div class="session-medium" v-if="expandedSessions.has(session.session)">
                                        <div v-if="!session.medium || session.medium.toLowerCase() === 'direct' || session.medium.toLowerCase() === 'unknown'" class="medium-label medium-direct">
                                            <div>
                                                {{ $t('components.prospect_sessions.session.types.direct') }}
                                            </div>
                                        </div>

                                        <div v-else-if="session.medium.toLowerCase() === 'cpc'" class="medium-label medium-cpc">
                                            <div>
                                                {{ $t('components.prospect_sessions.session.types.cpc') }}
                                            </div>

                                            <span v-if="session.term">
                                                {{ $t('components.prospect_sessions.session.term', { term: session.term }) }}
                                            </span>
                                        </div>

                                        <div
                                            v-else-if="session.medium"
                                            class="medium-label"
                                            :class="`medium-${session.medium.toLowerCase()}`">
                                            <div>
                                                {{ $te('components.prospect_sessions.session.types.' + session.medium.toLowerCase()) ? $t('components.prospect_sessions.session.types.' + session.medium.toLowerCase()) : session.medium  }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div class="session-details">
                                <div class="session-source" v-if="!(!session.medium || session.medium.toLowerCase() === 'direct' || session.medium.toLowerCase() === 'unknown') && expandedSessions.has(session.session)">
                                    <div class="source-logo" v-if="session.source_logo">
                                        <AppLogo :url="session.source_logo" />
                                    </div>

                                    <div class="source-details">
                                        <div class="source-name">
                                            {{ session.source || $t('common.source') + ':' }}
                                        </div>

                                        <a
                                            class="source-url"
                                            v-if="session.referer"
                                            :href="session.referer"
                                            target="_blank"
                                            rel="noreferrer external"
                                            @click.stop
                                        >
                                            {{ session.referer }}
                                        </a>
                                    </div>
                                </div>
                                
                                <div v-if="expandedSessions.has(session.session)" class="session-device">
                                    <div class="device-language" v-if="session.language">
                                        {{ session.language }}
                                    </div>

                                    <div class="device-type" v-if="session.device_type && (session.device_type.toLowerCase() === 'desktop' || session.device_type.toLowerCase() === 'mobile')">
                                        {{ session.device_type }}
                                    </div>

                                    <div class="device-platform" v-if="session.platform">
                                        {{ session.platform }}
                                    </div>

                                    <div class="device-browser" v-if="session.browser">
                                        {{ session.browser }}
                                    </div>

                                    <div class="device-ua" v-if="session.user_agent">
                                        {{ session.user_agent }}
                                    </div>
                                </div>

                                <div class="session-pageviews" v-if="session.pageviews && session.pageviews.length > 0">
                                    <!-- <div class="h6 pageviews-label">{{ $t('components.prospect_sessions.session.pageviews_label') }}</div> -->
                                    <div class="pageview" v-for="(pageview, pIndex) in session.pageviews" :key="pIndex">
                                        <span class="p visit-date">{{ timestampToDisplayDate(pageview.last_visit, false, false, true) }}</span>
                                        <a :href="pageview.page_url.split('?')[0]" target="_blank" rel="noreferrer external">
                                            <div class="h6 pageview-title" v-if="pageview.page_title" :title="pageview.page_title">{{ pageview.page_title }}</div>
                                            <div class="p pageview-url" :title="pageview.page_url">{{ pageview.page_url }}</div>
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </WidgetSection>
</template>

<script>
    import { useProspectStore } from '@/stores/prospect';
    import { useAccountStore } from '@/stores/account';
    import { formatDateReverse } from '@/utils/formatting';
    import { epochToDisplayDate, timestampToDisplayDate } from '@/utils/dates';
    import WidgetSection from '@/components/sections/WidgetSection';
    import AppLogo from '@/components/misc/AppLogo';

    export default {
        name: "ProspectSessions",
        components: {
            WidgetSection,
            AppLogo
        },
        props: {
            auto: {
                type: Boolean,
                default: false
            },
            defaultToggle: {
                type: Boolean,
                default: false
            },
            paywall: {
                type: Boolean,
                default: false
            },
            cssClassWidgetSection: {
                type: String,
                required: false
            }
        },
        data() {
            return {
                loading: false,
                loaded: false,
                abortController: null,
                timeoutId: null,
                isActive: true,
                expandedSessions: new Set()
            }
        },
        computed: {
            paywallActive() {
                return this.paywall && useAccountStore().paywall;
            },
            unavailable() {
                return this.loaded && (!this.sessions || this.sessions.length < 1);
            },
            sessions() {
                return useProspectStore().sessions;
            },
            isAllExpanded() {
                return this.sessions.length > 0 && this.expandedSessions.size === this.sessions.length;
            },
            isAllCollapsed() {
                return this.expandedSessions.size === 0;
            }
        },
        methods: {
            epochToDisplayDate,
            timestampToDisplayDate,
            formatDateReverse,
            toggleSession(sessionId) {
                if (this.expandedSessions.has(sessionId)) {
                    this.expandedSessions.delete(sessionId);
                } else {
                    this.expandedSessions.add(sessionId);
                }
            },
            expandAll() {
                this.expandedSessions = new Set(this.sessions.map(s => s.session));
            },
            collapseAll() {
                this.expandedSessions = new Set();
            },
            initializeExpandedSessions() {
                if (this.sessions.length < 2) {
                    this.expandedSessions = new Set(this.sessions.map(s => s.session));
                }
            },
            sameDayAsPrevious(sessions, index) {
                if(index === 0) { return false; }

                const currentDate = new Date(sessions[index].session_first_visit * 1000);
                const previousDate = new Date(sessions[index - 1].session_first_visit * 1000);

                return currentDate.getFullYear() === previousDate.getFullYear() &&
                       currentDate.getMonth() === previousDate.getMonth() &&
                       currentDate.getDate() === previousDate.getDate();
            },
            reformatDuration(durationInSeconds) {
                const minutes = Math.floor(durationInSeconds / 60);
                const seconds = durationInSeconds % 60;

                let result = '';
                if (minutes > 0) {
                    result += `${minutes}m `;
                }
                result += `${seconds}s`;

                return result.trim();
            },
            async fetchSessions() {
                if (this.abortController) {
                    this.abortController.abort();
                    this.abortController = null;
                }

                this.abortController = new AbortController();

                this.loading = true;
                await useProspectStore().fetchSessions(this.abortController.signal);
                if(!this.isActive) { return; }
                this.loading = false;
                this.loaded = true;
                this.abortController = null;
                this.initializeExpandedSessions();
            },
            async handleLoadingStart() {
                if (!this.paywallActive && !this.loaded) {
                    await this.fetchSessions();
                    
                    if(!this.unavailable && this.$refs.WidgetSection) {
                        this.$refs.WidgetSection.toggleSection();
                    }
                }
            }
        },
        mounted() {
            if(!this.paywallActive && !this.loaded && (this.auto || this.defaultToggle)) {
                this.loading = true;
                this.timeoutId = setTimeout(() => {
                    this.fetchSessions();
                }, 300);
            }
        },
        unmounted() {
            this.isActive = false;

            if (this.abortController) {
                this.abortController.abort();
                this.abortController = null;
            }
            if (this.timeoutId) {
                clearTimeout(this.timeoutId);
            }
        }
    }
</script>